import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  PersonAddAltOutlined,
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
  MoreVert,
  Delete,
  ExitToApp,
  CheckBox,
  NotInterested,
  Fingerprint,
} from "@mui/icons-material";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useSelector, useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";

//Services
import DriverApi from "Services/Driver";

//Modals
import DriverAddModal from "Pages/Modals/Driver/Create";
import DriverUpdateModal from "Pages/Modals/Driver/Update";
import DriverDeleteModal from "Pages/Modals/Driver/Delete";

const Driver = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const demoaccount_id = useSelector((state) => state?.AuthReducer?.demoaccount_id || "");
  const userType = useSelector((state) => state?.AuthReducer?.userType || "");
  const subcmpnyName = useSelector((state) => state?.AuthReducer?.subCmpny || "");

  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setbulkrow] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [drivermodelId, setDrivermodelId] = useState("");

  // Menu state for actions dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDriver, setCurrentDriver] = useState(null);

  const handleMenuOpen = (event, driverRow) => {
    setAnchorEl(event.currentTarget);
    setCurrentDriver(driverRow);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentDriver(null);
  };

  const handleSignOutDriver = () => {
    if (currentDriver) {
      const drivermodelIdVal = currentDriver?._id;
      dispatch(showLoader("Signing out driver..."));
      const onSuccess = (res) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Driver signed out successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        handleMenuClose();
        getDrivers();
      };
      const onFailure = (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to sign out driver",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        handleMenuClose();
      };
      DriverApi.signOutDriver(drivermodelIdVal, { userId: drivermodelIdVal }).then(onSuccess, onFailure);
    }
  };

  const handleDeleteFromMenu = () => {
    if (currentDriver) {
      handleOpenDeleteDriverModal(currentDriver);
    }
    handleMenuClose();
  };

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [addDriverModal, setAddDriverModal] = useState(false);
  const [updateDriverModal, setDriverUpdateModal] = useState(false);
  const [DeleteDriverModal, setDriverDeleteModal] = useState(false);

  const handleOpenAddDriverModal = () => setAddDriverModal(true);
  const handleCloseAddDriverModal = () => {
    setAddDriverModal(false);
    getDrivers();
  };

  const handleOpenUpdateDriverModal = (rowItem) => {
    setDriverId(rowItem?._id);
    setDriverUpdateModal(true);
  };

  const handleCloseUpdateDriverModal = () => {
    setDriverUpdateModal(false);
    getDrivers();
  };

  const handleOpenDeleteDriverModal = (rowItem) => {
    setDriverId(rowItem?._id);
    setDrivermodelId(rowItem?.driver_ride_details?._id);
    setDriverDeleteModal(true);
  };

  const handleCloseDeleteDriverModal = () => {
    setDriverDeleteModal(false);
    getDrivers();
  };

  // Get Drivers list
  const getDrivers = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize, search: "" };
    if (demoaccount_id) {
      params.demoaccount_id = demoaccount_id;
    }
    DriverApi.getDriverList(params).then(onSuccess, onFailure);
  }, [page, pageSize, demoaccount_id, dispatch]);

  const getAllDrivers = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.data?.status === true) {
        setbulkrow(res?.data?.data?.data);
      } else {
        setbulkrow([]);
      }
    };
    const onFailure = () => {};
    DriverApi.getDriverList({ page: 1, limit: "unlimited", search: "" }).then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    getDrivers();
    getAllDrivers();
  }, [getDrivers, getAllDrivers]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const filteredRows = (row || []).filter((data) => {
    if (userType === "SUB COMPANY" && subcmpnyName) {
      if (data?.sub_companyName !== subcmpnyName) return false;
    }
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const fullName = `${data?.firstName || ""} ${data?.lastName || ""}`.toLowerCase();
    const mobile = `${data?.countryCode || ""} ${data?.mobileNumber || ""}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      data?.email?.toLowerCase().includes(searchLower) ||
      mobile.includes(searchLower) ||
      (data?.driver_ride_details?.accountStatus || "ONBOARDING").toLowerCase().includes(searchLower)
    );
  });

  const dataRows = filteredRows.map((data, index) => ({
    sno: calculateSno(page, pageSize, index),
    userName: data?.firstName + " " + data?.lastName,
    email: data?.email,
    mobile: (data?.countryCode || "") + " " + (data?.mobileNumber || ""),
    total_requests: data?.driver_ride_details?.total_requests?.length || 0,
    accepted_requests: data?.driver_ride_details?.accepted_requests?.length || 0,
    cancelled_requests: data?.driver_ride_details?.cancelled_requests?.length || 0,
    accountStatus: data?.driver_ride_details?.accountStatus || "ONBOARDING",
    row: data,
  }));

  const bulkdataRows = _.map(
    _.orderBy(bulkrow || [], ["createdAt"], ["asc"]),
    (data, index) => ({
      sno: index + 1,
      userName: data?.firstName + " " + data?.lastName,
      email: data?.email,
      mobile: (data?.countryCode || "") + " " + (data?.mobileNumber || ""),
      total_requests: data?.driver_ride_details?.total_requests?.length || 0,
      accepted_requests: data?.driver_ride_details?.accepted_requests?.length || 0,
      cancelled_requests: data?.driver_ride_details?.cancelled_requests?.length || 0,
      accountStatus: data?.driver_ride_details?.accountStatus || "ONBOARDING",
    })
  );

  const formatteddatas = dataRows.map((r) => ({
    "S.NO": r.sno,
    "User Name": r.userName,
    Email: r.email,
    Mobile: r.mobile,
    "Total Requests": r.total_requests,
    "Accepted Requests": r.accepted_requests,
    "Cancelled Requests": r.cancelled_requests,
    "Account Status": r.accountStatus,
  }));

  const bulkformatteddatas = bulkdataRows.map((r) => ({
    "S.NO": r.sno,
    "User Name": r.userName,
    Email: r.email,
    Mobile: r.mobile,
    "Total Requests": r.total_requests,
    "Accepted Requests": r.accepted_requests,
    "Cancelled Requests": r.cancelled_requests,
    "Account Status": r.accountStatus,
  }));

  // Export handlers
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All DriverList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    const header = ["S.NO", "User Name", "Email", "Mobile", "Total", "Accepted", "Cancelled", "Status"].join("\t");
    const rows = dataRows
      .map((r) =>
        [r.sno, r.userName, r.email, r.mobile, r.total_requests, r.accepted_requests, r.cancelled_requests, r.accountStatus].join("\t")
      )
      .join("\n");
    navigator.clipboard.writeText([header, rows].join("\n")).then(() => {
      alert("Table copied to clipboard!");
    });
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Drivers");
    XLSX.writeFile(workbook, "DriverList.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "DriverList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const tableData = dataRows.map((r) => [
      r.sno, r.userName, r.email, r.mobile, r.total_requests, r.accepted_requests, r.cancelled_requests, r.accountStatus,
    ]);
    doc.autoTable({
      head: [["S.NO", "User Name", "Email", "Mobile", "Total", "Accepted", "Cancelled", "Status"]],
      body: tableData,
    });
    doc.save("DriverList.pdf");
  };

  // Pagination
  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Drivers</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Drivers</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search drivers..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        <Box className={classes.rightActions}>
          <Box className={classes.exportGroup}>
            <Button className={classes.exportButton} onClick={handleCopy}>Copy</Button>
            <Button className={classes.exportButton} onClick={handleDownloadExcel}>Excel</Button>
            <Button className={classes.exportButton} onClick={handleDownloadCSV}>CSV</Button>
            <Button className={classes.exportButton} onClick={handleDownloadPDF}>PDF</Button>
            <Button className={classes.exportButton} onClick={handleDownloadAll}>All</Button>
          </Box>

          <Button
            variant="contained"
            className={classes.addButton}
            onClick={handleOpenAddDriverModal}
            startIcon={<PersonAddAltOutlined />}
          >
            Add Driver
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>User Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Email</TableCell>
              <TableCell className={classes.tableHeaderCell}>Mobile</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">Total Requests</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">Accepted</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">Cancelled</TableCell>
              <TableCell className={classes.tableHeaderCell}>Account Status</TableCell>
              <TableCell className={classes.tableHeaderCell} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.userName}
                </TableCell>
                <TableCell className={classes.tableCell}>{rowItem.email}</TableCell>
                <TableCell className={classes.tableCell}>{rowItem.mobile}</TableCell>
                <TableCell className={classes.tableCell} align="center">{rowItem.total_requests}</TableCell>
                <TableCell className={classes.tableCell} align="center">{rowItem.accepted_requests}</TableCell>
                <TableCell className={classes.tableCell} align="center">{rowItem.cancelled_requests}</TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.accountStatus === "APPROVED" ? (
                    <Box
                      className={classes.statusBadgeApproved}
                      onClick={() => handleOpenUpdateDriverModal(rowItem.row)}
                    >
                      <CheckBox fontSize="small" /> APPROVED
                    </Box>
                  ) : rowItem.accountStatus === "BANNED" ? (
                    <Box
                      className={classes.statusBadgeBanned}
                      onClick={() => handleOpenUpdateDriverModal(rowItem.row)}
                    >
                      <NotInterested fontSize="small" /> BANNED
                    </Box>
                  ) : (
                    <Box
                      className={classes.statusBadgeDefault}
                      onClick={() => handleOpenUpdateDriverModal(rowItem.row)}
                    >
                      <Fingerprint fontSize="small" /> {rowItem.accountStatus}
                    </Box>
                  )}
                </TableCell>
                <TableCell className={classes.tableCell} align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, rowItem.row)}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No drivers found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            if (currentDriver) handleOpenUpdateDriverModal(currentDriver);
            handleMenuClose();
          }}>
            <CheckBox style={{ marginRight: 8, fontSize: 18 }} /> Edit / Status
          </MenuItem>
          <MenuItem onClick={handleDeleteFromMenu}>
            <Delete style={{ marginRight: 8, fontSize: 18 }} /> Delete
          </MenuItem>
          <MenuItem onClick={handleSignOutDriver}>
            <ExitToApp style={{ marginRight: 8, fontSize: 18 }} /> Sign Out
          </MenuItem>
        </Menu>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>

      <DriverAddModal
        openDialog={addDriverModal}
        handleClose={handleCloseAddDriverModal}
      />
      <DriverUpdateModal
        openDialog={updateDriverModal}
        driverId={driverId}
        handleClose={handleCloseUpdateDriverModal}
      />
      <DriverDeleteModal
        openDialog={DeleteDriverModal}
        driverId={driverId}
        drivermodelId={drivermodelId}
        handleClose={handleCloseDeleteDriverModal}
      />
    </Box>
  );
};

export default Driver;
